/** Ключи настроек приветственного экрана (плоские поля в appTitleSettings). */
export const WELCOME_SCREEN_SETTING_KEYS = [
  'welcomeScreenText',
  'welcomeScreenSubtitleText',
  'welcomeScreenImages',
  'welcomeScreenFontSize',
  'welcomeScreenFontFamily',
  'welcomeScreenCustomFontUrl',
  'welcomeScreenCustomFontDataUrl',
  'welcomeScreenCustomFontName',
  'welcomeScreenGoogleFontFamilies',
  'welcomeScreenColor',
  'welcomeScreenUseGradient',
  'welcomeScreenGradientStart',
  'welcomeScreenGradientEnd',
  'welcomeScreenGlowEnabled',
  'welcomeScreenGlowColor',
  'welcomeScreenGlowIntensity',
  'welcomeScreenGlowSpread',
  'welcomeScreenEffectType',
  'welcomeScreenSubtitleFontSize',
  'welcomeScreenSubtitleColor',
  'welcomeScreenTextPositionX',
  'welcomeScreenTextPositionY',
  'welcomeScreenTextScale',
  'welcomeScreenSubtitlePositionX',
  'welcomeScreenSubtitlePositionY',
  'welcomeScreenSubtitleScale',
  'welcomeScreenDescriptionText',
  'welcomeScreenDescriptionFontSize',
  'welcomeScreenDescriptionFontFamily',
  'welcomeScreenDescriptionColor',
  'welcomeScreenDescriptionPositionX',
  'welcomeScreenDescriptionPositionY',
  'welcomeScreenDescriptionScale',
  'welcomeScreenDescriptionMaxWidth',
  'welcomeScreenBackgroundType',
  'welcomeScreenBackgroundGradient',
  'welcomeScreenBackgroundColor',
  'welcomeScreenBackgroundImage',
  'welcomeScreenSnowEnabled',
];

/**
 * Слой значений приветствия с учётом мобильных переопределений (welcomeScreenMobile).
 * Если ключа нет в mobile — берётся значение из base (десктоп).
 */
export function getWelcomeScreenLayer(base, mobileSlice) {
  const m = mobileSlice && typeof mobileSlice === 'object' ? mobileSlice : null;
  const layer = {};
  for (const k of WELCOME_SCREEN_SETTING_KEYS) {
    layer[k] = m && Object.prototype.hasOwnProperty.call(m, k) ? m[k] : base[k];
  }
  return layer;
}

/** Позиции по умолчанию для мобильного экрана: по центру по X, колонка по Y (без наложения). */
export const MOBILE_WELCOME_DEFAULT_SUBTITLE_POSITION_X = 0;
export const MOBILE_WELCOME_DEFAULT_SUBTITLE_POSITION_Y = -68;
export const MOBILE_WELCOME_DEFAULT_TITLE_POSITION_X = 0;
export const MOBILE_WELCOME_DEFAULT_TITLE_POSITION_Y = 42;

function mobileSliceHas(m, key) {
  return m && typeof m === 'object' && Object.prototype.hasOwnProperty.call(m, key);
}

/** Значение поля при редактировании мобильного слоя, если ключ ещё не задан в welcomeScreenMobile. */
export function getMobileUnsetWelcomeFieldFallback(base, key) {
  switch (key) {
    case 'welcomeScreenSubtitlePositionX':
      return MOBILE_WELCOME_DEFAULT_SUBTITLE_POSITION_X;
    case 'welcomeScreenSubtitlePositionY':
      return MOBILE_WELCOME_DEFAULT_SUBTITLE_POSITION_Y;
    case 'welcomeScreenTextPositionX':
      return MOBILE_WELCOME_DEFAULT_TITLE_POSITION_X;
    case 'welcomeScreenTextPositionY':
      return MOBILE_WELCOME_DEFAULT_TITLE_POSITION_Y;
    case 'welcomeScreenTextScale':
    case 'welcomeScreenSubtitleScale':
      return 100;
    default:
      return base[key];
  }
}

/**
 * Слой для мобильной модалки / мобильного превью: как getWelcomeScreenLayer, но координаты
 * названия и подзаголовка (и их масштаб), если не заданы в mobileSlice, берутся не с десктопа,
 * а как центр экрана (см. константы выше), чтобы на телефоне по умолчанию было по центру.
 */
export function getWelcomeScreenLayerForMobileViewport(base, mobileSlice) {
  const m = mobileSlice && typeof mobileSlice === 'object' ? mobileSlice : null;
  const layer = getWelcomeScreenLayer(base, mobileSlice);
  if (!mobileSliceHas(m, 'welcomeScreenSubtitlePositionX')) {
    layer.welcomeScreenSubtitlePositionX = MOBILE_WELCOME_DEFAULT_SUBTITLE_POSITION_X;
  }
  if (!mobileSliceHas(m, 'welcomeScreenSubtitlePositionY')) {
    layer.welcomeScreenSubtitlePositionY = MOBILE_WELCOME_DEFAULT_SUBTITLE_POSITION_Y;
  }
  if (!mobileSliceHas(m, 'welcomeScreenTextPositionX')) {
    layer.welcomeScreenTextPositionX = MOBILE_WELCOME_DEFAULT_TITLE_POSITION_X;
  }
  if (!mobileSliceHas(m, 'welcomeScreenTextPositionY')) {
    layer.welcomeScreenTextPositionY = MOBILE_WELCOME_DEFAULT_TITLE_POSITION_Y;
  }
  if (!mobileSliceHas(m, 'welcomeScreenTextScale')) {
    layer.welcomeScreenTextScale = 100;
  }
  if (!mobileSliceHas(m, 'welcomeScreenSubtitleScale')) {
    layer.welcomeScreenSubtitleScale = 100;
  }
  return layer;
}

/** Полные настройки приложения с подставленным слоем приветствия (для превью / рендера). */
export function buildAppTitleWithWelcomeLayer(baseSettings, layer) {
  return { ...baseSettings, ...layer };
}

/**
 * Для модалки приветствия на телефоне: welcomeScreenMobile поверх base; координаты названия
 * и подзаголовка без явного мобильного значения — центр экрана (не наследуют смещение с десктопа).
 */
export function resolveAppTitleSettingsForMobileWelcome(baseSettings) {
  if (!baseSettings) return baseSettings;
  const mobile =
    baseSettings.welcomeScreenMobile && typeof baseSettings.welcomeScreenMobile === 'object'
      ? baseSettings.welcomeScreenMobile
      : {};
  const layer = getWelcomeScreenLayerForMobileViewport(baseSettings, mobile);
  return buildAppTitleWithWelcomeLayer(baseSettings, layer);
}

/**
 * Слияние сохранённых appTitleSettings с дефолтами: корень shallow, welcomeScreenMobile глубоко.
 * Иначе после JSON с сервера объект welcomeScreenMobile «рвёт» вложенные поля (например список Google Fonts).
 */
export function mergeAppTitleSettingsFromStorage(defaults, saved) {
  if (!saved || typeof saved !== 'object') {
    return { ...defaults };
  }
  const out = { ...defaults, ...saved };
  const defM =
    defaults.welcomeScreenMobile && typeof defaults.welcomeScreenMobile === 'object'
      ? { ...defaults.welcomeScreenMobile }
      : {};
  const savM =
    saved.welcomeScreenMobile && typeof saved.welcomeScreenMobile === 'object' ? { ...saved.welcomeScreenMobile } : {};
  out.welcomeScreenMobile = { ...defM, ...savM };

  const defRootFonts = Array.isArray(defaults.welcomeScreenGoogleFontFamilies)
    ? defaults.welcomeScreenGoogleFontFamilies
    : [];
  out.welcomeScreenGoogleFontFamilies = Array.isArray(saved.welcomeScreenGoogleFontFamilies)
    ? [...saved.welcomeScreenGoogleFontFamilies]
    : [...defRootFonts];

  const defMobFonts = Array.isArray(defM.welcomeScreenGoogleFontFamilies)
    ? defM.welcomeScreenGoogleFontFamilies
    : [];
  out.welcomeScreenMobile.welcomeScreenGoogleFontFamilies = Array.isArray(savM.welcomeScreenGoogleFontFamilies)
    ? [...savM.welcomeScreenGoogleFontFamilies]
    : [...defMobFonts];

  const defSb = Array.isArray(defaults.sidebarGoogleFontFamilies) ? defaults.sidebarGoogleFontFamilies : [];
  out.sidebarGoogleFontFamilies = Array.isArray(saved.sidebarGoogleFontFamilies)
    ? [...saved.sidebarGoogleFontFamilies]
    : [...defSb];

  return out;
}

function uniqFontFamilies(arr) {
  const list = Array.isArray(arr) ? arr.map((x) => String(x).trim()).filter(Boolean) : [];
  return [...new Set(list)];
}

/** Объединить списки Google Fonts из локального и серверного снимка (чтобы перезагрузка не затирала локальные добавления). */
export function unionWelcomeGoogleFontFamiliesInSettings(defaults, serverSaved, localSaved) {
  const serverM = mergeAppTitleSettingsFromStorage(defaults, serverSaved || {});
  if (!localSaved || typeof localSaved !== 'object') {
    return serverM;
  }
  const localM = mergeAppTitleSettingsFromStorage(defaults, localSaved);
  const all = uniqFontFamilies([
    ...serverM.welcomeScreenGoogleFontFamilies,
    ...localM.welcomeScreenGoogleFontFamilies,
    ...(serverM.welcomeScreenMobile?.welcomeScreenGoogleFontFamilies || []),
    ...(localM.welcomeScreenMobile?.welcomeScreenGoogleFontFamilies || []),
  ]);
  const merged = { ...serverM, welcomeScreenGoogleFontFamilies: [...all] };
  merged.welcomeScreenMobile = {
    ...(serverM.welcomeScreenMobile || {}),
    welcomeScreenGoogleFontFamilies: [...all],
  };

  const sidebarAll = uniqFontFamilies([
    ...(Array.isArray(serverM.sidebarGoogleFontFamilies) ? serverM.sidebarGoogleFontFamilies : []),
    ...(Array.isArray(localM.sidebarGoogleFontFamilies) ? localM.sidebarGoogleFontFamilies : []),
  ]);
  merged.sidebarGoogleFontFamilies = sidebarAll;

  return merged;
}
